<template>
  <section>
    <div class="panel">
      <h2>Sites e Acessos</h2>
      <p class="muted">
        Cadastro de domínios autorizados para automação de navegador (somente HTTPS). Com o
        <strong>modo segundo eu</strong> ligado em Configurações, a {{ assistantName }} pode navegar em qualquer
        site HTTPS sem esta lista.
      </p>

      <form class="form" @submit.prevent="createSite">
        <label>
          Nome
          <input v-model="form.name" required maxlength="120" />
        </label>
        <label>
          Domínios (separados por vírgula)
          <input v-model="form.domains" required placeholder="exemplo.com.br" />
        </label>
        <label>
          URL inicial (HTTPS)
          <input v-model="form.startUrl" type="url" required placeholder="https://exemplo.com.br/" />
        </label>
        <label>
          Finalidade
          <input v-model="form.purpose" required maxlength="500" />
        </label>
        <label class="check">
          <input v-model="form.requiresApproval" type="checkbox" />
          Exige aprovação antes de executar
        </label>
        <button type="submit" :disabled="saving">Cadastrar site</button>
      </form>
      <p class="muted" v-if="message">{{ message }}</p>
    </div>

    <div class="panel" style="margin-top: 1rem">
      <h2>Sites cadastrados</h2>
      <ul class="plain">
        <li v-for="site in sites" :key="site.id">
          <strong>{{ site.name }}</strong>
          <span class="muted"> · {{ site.domains.join(', ') }}</span>
          <p class="muted">{{ site.purpose }}</p>
          <p class="muted">
            ops: {{ site.allowedOperations.join(', ') }} · aprovação:
            {{ site.requiresApproval ? 'sim' : 'não' }} ·
            {{ site.active ? 'ativo' : 'inativo' }}
          </p>
        </li>
      </ul>
      <p class="muted" v-if="!sites.length">Nenhum site cadastrado ainda.</p>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { assistantName } from '../assistant-name';

type Site = {
  id: string;
  name: string;
  domains: string[];
  purpose: string;
  allowedOperations: string[];
  requiresApproval: boolean;
  active: boolean;
};

const sites = ref<Site[]>([]);
const saving = ref(false);
const message = ref('');
const form = reactive({
  name: '',
  domains: '',
  startUrl: '',
  purpose: '',
  requiresApproval: true,
});

async function loadSites() {
  const res = await fetch('/api/browser/sites', { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = (await res.json()) as { sites: Site[] };
  sites.value = data.sites ?? [];
}

async function createSite() {
  saving.value = true;
  message.value = '';
  try {
    const res = await fetch('/api/browser/sites', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        name: form.name,
        domains: form.domains
          .split(',')
          .map((d) => d.trim().toLowerCase())
          .filter(Boolean),
        startUrl: form.startUrl,
        purpose: form.purpose,
        allowedOperations: ['navigate', 'read_page', 'extract_text'],
        requiresAuth: false,
        requiresApproval: form.requiresApproval,
        active: true,
      }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${res.status}`);
    }
    form.name = '';
    form.domains = '';
    form.startUrl = '';
    form.purpose = '';
    message.value = 'Site cadastrado.';
    await loadSites();
  } catch (err) {
    message.value = err instanceof Error ? err.message : 'Falha ao cadastrar';
  } finally {
    saving.value = false;
  }
}

onMounted(() => {
  void loadSites().catch((err) => {
    message.value = err instanceof Error ? err.message : 'Falha ao carregar sites';
  });
});
</script>

<style scoped>
.form {
  display: grid;
  gap: 0.75rem;
  margin-top: 1rem;
}
label {
  display: grid;
  gap: 0.35rem;
  font-size: 0.92rem;
}
input[type='text'],
input[type='url'],
input:not([type='checkbox']) {
  border: 1px solid var(--line);
  border-radius: 0.6rem;
  padding: 0.55rem 0.7rem;
  font: inherit;
  background: #fff;
}
.check {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
button {
  justify-self: start;
  border: 0;
  border-radius: 0.6rem;
  padding: 0.6rem 1rem;
  background: #2f6f57;
  color: #fff;
  font: inherit;
  cursor: pointer;
}
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
